const MANUAL_PAGE_SIZE = 10;

class ManualDownloadRoot extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-manual-search-form]');
    this.input = this.querySelector('[data-manual-search-input]');
    this.list = this.querySelector('[data-manual-list]');
    this.rows = this.list ? Array.from(this.list.querySelectorAll('[data-manual-row]')) : [];
    this.loadBtn = this.querySelector('[data-manual-load-more]');
    this.emptyState = this.querySelector('[data-manual-empty]');
    this.visibleCap = Math.min(MANUAL_PAGE_SIZE, this.rows.length);
    this.lastQuery = '';

    this.onInput = () => {
      const q = this.getQuery();
      if (this.lastQuery !== '' && q === '') {
        this.visibleCap = Math.min(MANUAL_PAGE_SIZE, this.rows.length);
      }
      this.lastQuery = q;
      this.updateDisplay();
    };

    this.input?.addEventListener('input', this.onInput);
    this.input?.addEventListener('search', this.onInput);
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onInput();
    });

    this.loadBtn?.addEventListener('click', () => {
      const matched = this.matchingRows();
      this.visibleCap = Math.min(this.visibleCap + MANUAL_PAGE_SIZE, matched.length);
      this.updateDisplay();
    });

    this.addEventListener('click', this.onBlobDownloadClick);

    this.updateDisplay();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onBlobDownloadClick);
  }

  onBlobDownloadClick = (e) => {
    const trigger = e.target.closest('[data-manual-blob-download]');
    if (!trigger || !this.contains(trigger)) return;
    e.preventDefault();
    const url = trigger.getAttribute('href');
    if (!url) return;
    const filename = trigger.dataset.suggestedFilename || 'manual.pdf';
    ManualDownloadRoot.tryBlobDownload(url, filename).catch(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  };

  static async tryBlobDownload(url, filename) {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  }

  getQuery() {
    return ManualDownloadRoot.normalizeSearchText(this.input?.value || '');
  }

  static normalizeSearchText(s) {
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  static escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  static escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Phrase match with flexible whitespace between tokens (aligned with normalized search).
   */
  static buildHighlightRegex(normalizedQuery) {
    const parts = normalizedQuery.split(/\s+/).filter(Boolean);
    if (!parts.length) return null;
    const body = parts.map(ManualDownloadRoot.escapeRegExp).join('\\s+');
    try {
      return new RegExp(`(${body})`, 'gi');
    } catch {
      return null;
    }
  }

  static applyHighlights(text, re) {
    if (!text || !re) return ManualDownloadRoot.escapeHtml(text);
    let result = '';
    let last = 0;
    const matches = text.matchAll(re);
    for (const m of matches) {
      result += ManualDownloadRoot.escapeHtml(text.slice(last, m.index));
      result += `<mark class="manual-download__mark">${ManualDownloadRoot.escapeHtml(m[0])}</mark>`;
      last = m.index + m[0].length;
    }
    result += ManualDownloadRoot.escapeHtml(text.slice(last));
    return result;
  }

  syncRowHighlight(row, q, isVisible) {
    const titleEl = row.querySelector('[data-manual-hl="title"]');
    const skuEl = row.querySelector('[data-manual-hl="sku"]');
    const setPlain = () => {
      if (titleEl) titleEl.textContent = titleEl.getAttribute('data-raw') || '';
      if (skuEl) skuEl.textContent = skuEl.getAttribute('data-raw') || '';
    };
    if (!isVisible || !q) {
      setPlain();
      return;
    }
    const re = ManualDownloadRoot.buildHighlightRegex(q);
    if (!re) {
      setPlain();
      return;
    }
    const titleRaw = titleEl?.getAttribute('data-raw') || '';
    const skuRaw = skuEl?.getAttribute('data-raw') || '';
    if (titleEl) titleEl.innerHTML = ManualDownloadRoot.applyHighlights(titleRaw, re);
    if (skuEl) skuEl.innerHTML = ManualDownloadRoot.applyHighlights(skuRaw, re);
  }

  matchingRows() {
    const q = this.getQuery();
    if (!q) return this.rows;
    return this.rows.filter((row) => {
      const hay = ManualDownloadRoot.normalizeSearchText(row.getAttribute('data-search') || '');
      return hay.includes(q);
    });
  }

  updateDisplay() {
    const q = this.getQuery();
    const matched = this.matchingRows();

    this.rows.forEach((row) => {
      const idx = matched.indexOf(row);
      let show = false;
      if (idx === -1) {
        show = false;
      } else if (q) {
        show = true;
      } else {
        show = idx < this.visibleCap;
      }
      row.hidden = !show;
      if (show) {
        row.removeAttribute('aria-hidden');
      } else {
        row.setAttribute('aria-hidden', 'true');
      }
      this.syncRowHighlight(row, q, show);
    });

    if (this.emptyState) {
      const noMatches = this.rows.length > 0 && matched.length === 0;
      this.emptyState.hidden = !noMatches;
    }

    if (this.loadBtn) {
      const hideLoadMore = q !== '' || matched.length <= this.visibleCap;
      this.loadBtn.hidden = hideLoadMore;
    }
  }
}

if (!customElements.get('manual-download-root')) {
  customElements.define('manual-download-root', ManualDownloadRoot);
}
